import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AdoService } from './ado.service';
import { AdoReleaseEnvironment } from '../models/release';
import { EnvironmentService } from './environment.service';
import { finalize } from 'rxjs/operators';
import { LoadingService } from '../services/loading.service';

interface AdoEnvironmentMapping {
  releaseEnvId: number;
  adoEnvId: number;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReleaseEnvironmentService {
  private environmentMappings: AdoEnvironmentMapping[] = [];
  private readonly orgName: string;
  private readonly projectName: string;

  constructor(
    private readonly adoService: AdoService,
    private readonly environmentService: EnvironmentService,
    private readonly loadingService: LoadingService
  ) {
    const environment = this.environmentService.getEnvironment();
    this.orgName = environment.defaultOrg;
    this.projectName = environment.defaultProject;
  }

  /**
   * Load the release snapshot from ADO to get the environment IDs
   */
  loadReleaseSnapshot(adoReleaseId: string): Observable<any> {
    if (!adoReleaseId || !this.orgName || !this.projectName) {
      console.warn('Missing required data to fetch release snapshot');
      return new Observable(subscriber => subscriber.complete());
    }
    
    console.log(`Fetching release snapshot for: ${adoReleaseId} in ${this.orgName}/${this.projectName}`);
    
    this.loadingService.show('releaseSnapshot');
    
    return this.adoService.GetReleaseById(
      this.orgName,
      this.projectName,
      adoReleaseId
    ).pipe(
      finalize(() => this.loadingService.hide('releaseSnapshot'))
    );
  }

  /**
   * Map environment names to their ADO environment IDs from the release snapshot
   */
  mapEnvironmentsFromSnapshot(adoEnvironments: any[], releaseEnvironments: AdoReleaseEnvironment[]): AdoEnvironmentMapping[] {
    const mappings: AdoEnvironmentMapping[] = [];
    
    console.log('Release snapshot environments:', adoEnvironments);
    console.log('Our release environments:', releaseEnvironments);
    
    // Return empty array if either parameter is null/undefined or not an array
    if (!adoEnvironments || !Array.isArray(adoEnvironments) || !releaseEnvironments || !Array.isArray(releaseEnvironments)) {
      console.warn('Invalid inputs to mapEnvironmentsFromSnapshot');
      return mappings;
    }
    
    releaseEnvironments.forEach((env: AdoReleaseEnvironment) => {
      const matchingAdoEnv = adoEnvironments.find(
        (adoEnv: any) => adoEnv.name.toLowerCase() === env.Name.toLowerCase()
      );
      
      if (matchingAdoEnv) {
        console.log(`Found matching environment: ${env.Name} => ADO ID: ${matchingAdoEnv.id}, Our ID: ${env.Id}`);
        mappings.push({
          releaseEnvId: env.Id,
          adoEnvId: matchingAdoEnv.id,
          name: env.Name
        });
      } else {
        console.warn(`No matching ADO environment found for: ${env.Name}`);
      }
    });
    
    this.environmentMappings = mappings;
    return mappings;
  }

  /**
   * Filter environments to get only failing ones
   */
  getFailingEnvironments(environments: AdoReleaseEnvironment[]): AdoReleaseEnvironment[] {
    if (!environments || !Array.isArray(environments)) {
      return [];
    }
    
    const isDefaultDate = (dateStr: string): boolean => {
      if (!dateStr) return true;
      
      return dateStr === '0001-01-01T00:00:00' || 
             dateStr === '0001-01-01T00:00:00Z' ||
             dateStr === '0001-01-01T00:00:00.0000000' ||
             dateStr === '0001-01-01T00:00:00.0000000Z';
    };
    
    return environments.filter(env => 
      (env.RejectedDateTimeUtc && !isDefaultDate(env.RejectedDateTimeUtc)) || env.Status?.toLowerCase().includes('reject')
    );
  }

  /**
   * Generate Azure DevOps environment URL for a specific environment
   */
  getEnvironmentUrl(environmentName: string, adoReleaseId: string, releaseEnvironments: AdoReleaseEnvironment[]): string {
    if (!this.orgName || !this.projectName || !adoReleaseId) {
      console.warn('Missing required data for environment URL:', 
        { orgName: this.orgName, projectName: this.projectName, releaseId: adoReleaseId });
      return '';
    }
    
    // Find the matching environment mapping
    const mapping = this.environmentMappings.find(
      m => m.name.toLowerCase() === environmentName.toLowerCase()
    );
    
    if (mapping) {
      console.log(`Using mapped ADO environment ID for ${environmentName}: ${mapping.adoEnvId}`);
      return `https://dev.azure.com/${this.orgName}/${this.projectName}/_releaseProgress?releaseId=${adoReleaseId}&_a=release-environment-logs&environmentId=${mapping.adoEnvId}`;
    }
    
    console.warn(`Using name-based fallback URL for ${environmentName}`);
    return `https://dev.azure.com/${this.orgName}/${this.projectName}/_releaseProgress?releaseId=${adoReleaseId}`;
  }

  /**
   * Try different methods to get a valid environment URL in case the primary method fails
   */
  getEnvironmentUrlFallback(environmentName: string, adoReleaseId: string, releaseEnvironments: AdoReleaseEnvironment[]): string {
    const primaryUrl = this.getEnvironmentUrl(environmentName, adoReleaseId, releaseEnvironments);
    if (primaryUrl) {
      return primaryUrl;
    }
    
    if (this.orgName && this.projectName && adoReleaseId) {
      console.warn(`Using name-based fallback URL for ${environmentName}`);
      return `https://dev.azure.com/${this.orgName}/${this.projectName}/_releaseProgress?releaseId=${adoReleaseId}&environmentName=${encodeURIComponent(environmentName)}`;
    }
    
    return '';
  }
} 